import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  addPatientSchema,
  AddPatientSchema,
} from "../../lib/validation/add-patient";
import { cn } from "../../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { CalendarIcon, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "../../components/ui/calendar";
import { useAddPatient } from "../../lib/hooks/patient/use-add-patient";
import { useAppointmentData } from "../../contexts/appointment-data-context";
import { useAuth } from "../../contexts/auth-context";
import { useState } from "react";
import BackIcon from "../../components/back-icon";

interface PopaddPatientProps {
  onClose: (newPatient?: { patientId: string; name: string }) => void;
}


function PopaddPatient({ onClose }: PopaddPatientProps) {
  const [searchParams] = useSearchParams();
  const backUrl = searchParams.get("backUrl") ?? "/doctors";
  const navigate = useNavigate();
  const form = useForm<AddPatientSchema>({
    resolver: zodResolver(addPatientSchema),
  });

  const [date, setDate] = useState<Date | null>(null);
  const [dobOrAgeErr, setDobOrAgeErr] = useState("");

  const { mobile } = useAuth();
  const { data: apptData, setData } = useAppointmentData();
  const { addPatient, isPending } = useAddPatient();

  const calculateAge = (dob: Date) => {
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDifference = today.getMonth() - dob.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  const handleAddPatient = (data: AddPatientSchema) => {
    if (!data.dob && !data.age) {
      setDobOrAgeErr("Please enter either DOB or Age.");
      return;
    }

    addPatient(
      { ...data, mobile: mobile ?? "" },
      {
        onSuccess: (newData) => {
          localStorage.setItem("selectedPatient", newData.name);
          form.reset();
          setData({ ...apptData, patient: { id: newData.patientId, name: newData.name }, branchDetails: apptData?.branchDetails || [] });
          //queryClient.invalidateQueries({ queryKey: ["patients", mobile] });
          onClose({ patientId: newData.patientId, name: newData.name });
        },
      }
    );
  };

  return (
    <div className="container mx-auto flex flex-col min-h-screen max-w-md">
      <div className="mb-4 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onClose()}
          className="relative group"
        >
          <BackIcon />
          <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:flex items-center p-1 rounded text-xs text-white bg-gray-800 shadow-lg z-50">
            Back
          </span>
        </Button>
        <h1 className="text-lg font-bold">Add a Patient</h1>
      </div>
      <Form {...form}>
        <form
          onSubmit={(e) => {
            const values = form.watch();
            if (!values.dob && !values.age) setDobOrAgeErr("Please enter either DOB or Age");

            form.handleSubmit(handleAddPatient)(e);
          }}
          className="space-y-6"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    placeholder="Patient Name or ABHA ID"
                    {...field}
                    onKeyDown={(e) => {
                      if (!/^[a-zA-Z\s]*$/.test(e.key)) e.preventDefault(); // Restrict input to letters and spaces
                    }}
                    disabled={isPending}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <FormField
                control={form.control}
                name="age"
                render={({ field }) => (
                  <FormItem className="w-24">
                    <FormControl>
                      <Input
                        placeholder="Age"
                        type="number"
                        {...field}
                        onChange={(e) => {
                          if (parseInt(e.target.value, 10) < 0) return;
                          field.onChange(e);
                          setDobOrAgeErr("");
                        }}
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <span>or</span>
              <FormField
                control={form.control}
                name="dob"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Popover>
                        <PopoverTrigger asChild disabled={isPending}>
                          <Button
                            variant="outline"
                            className={cn(
                              "h-14 w-full text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? format(field.value, "dd MMMM yyyy") : <span>DOB</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="center">
                          <Calendar
                            mode="single"
                            captionLayout="dropdown"
                            selected={date || field.value}
                            onSelect={(selectedDate) => {
                              setDate(selectedDate!);
                              field.onChange(selectedDate);
                              setDobOrAgeErr("");
                              if (selectedDate) {
                                form.setValue("age", String(calculateAge(selectedDate)));
                              }
                            }}
                            fromYear={new Date().getFullYear() - 120}
                            toDate={new Date()}
                          />
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {dobOrAgeErr && <p className="text-sm text-red-500">{dobOrAgeErr}</p>}
          </div>

          <FormField
            control={form.control}
            name="gender"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Male</SelectItem>
                      <SelectItem value="F">Female</SelectItem>
                      <SelectItem value="O">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex gap-4">
            <FormField
              control={form.control}
              name="height"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Height (cm)"
                      type="number"
                      {...field}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="weight"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Weight (kg)"
                      type="number"
                      {...field}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Allergies */}
          <FormField
            control={form.control}
            name="allergies"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input placeholder="Any Allergies" {...field} disabled={isPending} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="pastIllness"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    placeholder="Any Past Illness or Procedures"
                    {...field}
                    disabled={isPending}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full" size="lg" disabled={isPending}>
            {isPending && <Loader2 className="mr-2 animate-spin" />}
            Continue
          </Button>
        </form>
      </Form>
    </div>
  );
}

export default PopaddPatient;
